// PressLogos component - Seamless infinite scroll animation with real SVG logos
// Fixed: Eliminated flickering by removing conflicting GPU compositing hints,
// moving filter+scale to wrapper div, and using 3 logo sets for smoother loop.
// Updated: Dynamic gradient background color for event pages

interface PressLogosProps {
  backgroundColor?: string; // CSS color value for gradient fade (e.g., 'rgb(255, 245, 245)' or '#fff5f5')
}

const PressLogos = ({ backgroundColor = '#E0E8F2' }: PressLogosProps) => {
  // All logos with uniform heights
  const logos = [
    { name: "Derma Weekly", src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/press-logo-derma-weekly-gwBiCSDZ5fd7tiHkBDGt6F.webp", height: "h-10 lg:h-12" },
    { name: "Hair Science Today", src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/press-logo-hair-science-DzjnVMpibYQNQgkQGoQyJs.webp", height: "h-10 lg:h-12" },
    { name: "The Wellness Edit", src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/press-logo-wellness-mag-RxX8Eovck7E6uuVA95srmS.webp", height: "h-10 lg:h-12" },
    { name: "Beauty Insider", src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/press-logo-beauty-insider-KJb4rZe59sjZhKBtkwBdA6.webp", height: "h-10 lg:h-12" },
    { name: "Men's Health Daily", src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/press-logo-mens-health-daily-4KseHjc34JXzdBuGw37zCy.webp", height: "h-10 lg:h-12" },
  ];

  // Convert hex to rgba for gradient transparency
  const hexToRgba = (hex: string, alpha: number = 1): string => {
    // If already rgb/rgba format, handle it
    if (hex.startsWith('rgb')) {
      const match = hex.match(/[\d.]+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      }
      return hex;
    }
    
    // Handle hex format
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const solidColor = backgroundColor;
  const transparentColor = hexToRgba(backgroundColor, 0);

  const isDarkBg = backgroundColor.includes('424680') || backgroundColor.includes('2d3a5c');
  const imgFilter = isDarkBg ? 'brightness(0) invert(1)' : 'brightness(0)';

  const LogoSet = ({ setKey }: { setKey: string }) => (
    <div className="flex items-center shrink-0" aria-hidden={setKey !== 'a'}>
      {logos.map((logo, index) => (
        <div
          key={`${setKey}-${index}`}
          className="px-8 lg:px-12 flex items-center justify-center shrink-0"
          style={{ transform: 'scale(1.35)' }}
        >
          <img
            src={logo.src}
            alt={setKey === 'a' ? logo.name : ''}
            className={`${logo.height} w-auto object-contain pointer-events-none select-none`}
            style={{ filter: imgFilter }}
            loading="eager"
            draggable={false}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative w-full overflow-hidden py-6 lg:py-8">
      {/* Gradient fade on left */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-20 lg:w-32 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to right, ${solidColor}, ${transparentColor})` }}
      />
      
      {/* Gradient fade on right */}
      <div 
        className="absolute right-0 top-0 bottom-0 w-20 lg:w-32 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to left, ${solidColor}, ${transparentColor})` }}
      />
      
      {/* Scrolling container - three identical sets for seamless loop
          Using transform: translateZ(0) on the single parent only to promote
          the whole strip to one GPU layer. No per-image GPU hints. */}
      <div 
        className="flex animate-marquee"
        style={{ transform: 'translateZ(0)' }}
      >
        <LogoSet setKey="a" />
        <LogoSet setKey="b" />
        <LogoSet setKey="c" />
      </div>
    </div>
  );
};

export default PressLogos;
